import { Group, InlineLink, Text, Title } from "@assetwatch/ui";
import { Card } from "@assetwatch/ui/experimental";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy } from "@fortawesome/sharp-solid-svg-icons";
import { useTranslation } from "react-i18next";
import type { RecentWin, RecentWinsResponse } from "../api/enterpriseClient";
import { MetricHighlightDeck } from "./shared/MetricHighlightDeck";
import { panelHeaderStyles } from "./shared/panelHeader";
import { useOpenFacilityDrawer } from "./useOpenFacilityDrawer";

type Props = {
  data: RecentWinsResponse;
  thirtyDaySavings: number;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const winsListClassName = "grid gap-(--spacing-12)";
const winCardClassName =
  "flex flex-row items-start gap-(--spacing-16) rounded-(--radius-lg) border border-outline-subtle bg-surface-container-low p-(--spacing-16)";
const winContentClassName = "flex min-w-0 flex-1 flex-col gap-(--spacing-8)";
const winHeaderRowClassName =
  "flex flex-wrap items-baseline gap-(--spacing-10)";
const winAmountClassName =
  "text-(length:--font-size-title-xl) font-bold leading-(--line-height-24) text-text-default";
const winActionsClassName = "flex w-[96px] shrink-0 flex-col gap-(--spacing-6)";

const formatDate = (dateStr: string | null, locale = "en-US") => {
  if (!dateStr) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

function buildMailtoHref(
  win: RecentWin,
  subject: string,
  greeting: string,
  resolvedFallback: string,
  costSavingsLine: string,
) {
  const lines = [
    greeting,
    "",
    win.heroSentence || resolvedFallback,
    "",
    win.resolutionSentence || (win.resolution ?? ""),
    "",
    costSavingsLine,
  ];

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
}

function WinCard({ win }: { win: RecentWin }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const resolvedDate = formatDate(win.dateResolved, locale);
  const openFacilityDrawer = useOpenFacilityDrawer();
  const mailtoHref = buildMailtoHref(
    win,
    win.machineName
      ? t("enterprise.wins.shareEmailSubjectMachine", {
          facility: win.facilityName,
          machine: win.machineName,
        })
      : t("enterprise.wins.shareEmailSubjectFacility", {
          facility: win.facilityName,
        }),
    t("enterprise.wins.shareEmailGreeting"),
    win.machineName
      ? t("enterprise.wins.shareEmailResolvedFallbackMachine", {
          facility: win.facilityName,
          machine: win.machineName,
        })
      : t("enterprise.wins.shareEmailResolvedFallbackFacility", {
          facility: win.facilityName,
        }),
    resolvedDate
      ? t("enterprise.wins.shareEmailCostSavingsLineWithDate", {
          amount: formatCurrency(win.savings),
          resolvedDate,
        })
      : t("enterprise.wins.shareEmailCostSavingsLine", {
          amount: formatCurrency(win.savings),
        }),
  );

  return (
    <article className={winCardClassName}>
      <div className={winContentClassName}>
        <div className={winHeaderRowClassName}>
          <span className={winAmountClassName}>
            {formatCurrency(win.savings)}
          </span>
          {resolvedDate ? (
            <Text as="span" size="sm" tone="subtle">
              {resolvedDate}
            </Text>
          ) : null}
        </div>
        <Text as="span" size="sm" tone="subtle">
          <InlineLink onClick={() => openFacilityDrawer(win.facilityId)}>
            {win.facilityName}
          </InlineLink>
          {win.machineName ? ` · ${win.machineName}` : null}
        </Text>
        {win.heroSentence || win.resolutionSentence ? (
          <Text size="sm">
            {win.heroSentence ? (
              <>
                {win.heroSentence}
                {win.resolutionSentence ? " " : ""}
              </>
            ) : null}
            {win.resolutionSentence ?? null}
          </Text>
        ) : null}
      </div>

      <div className={winActionsClassName}>
        <InlineLink
          href={mailtoHref}
          aria-label={t("enterprise.wins.ariaShareWin", {
            facility: win.facilityName,
          })}
        >
          {t("enterprise.wins.actionShare")}
        </InlineLink>
        <InlineLink
          href="https://app.assetwatch.com"
          target="_blank"
          aria-label={t("enterprise.wins.ariaLearnMore")}
        >
          {t("enterprise.wins.actionLearnMore")}
        </InlineLink>
      </div>
    </article>
  );
}

export function RecentWinsPanel({ data, thirtyDaySavings }: Props) {
  const { t } = useTranslation();

  return (
    <Card
      padding="none"
      className="flex flex-col gap-(--spacing-12) px-(--spacing-20) py-(--spacing-16)"
      data-testid="recent-wins-panel"
    >
      <div className={panelHeaderStyles.panelHeader}>
        <Group>
          <FontAwesomeIcon icon={faTrophy} />
          <Title size="lg">{t("enterprise.wins.panelTitle")}</Title>
          <Text as="div" size="sm" tone="subtle">
            {t("enterprise.wins.panelSubtitle")}
          </Text>
        </Group>
      </div>

      <MetricHighlightDeck
        items={[
          {
            id: "savings-30d",
            label: t("enterprise.wins.metric30DaySavings"),
            value: formatCurrency(thirtyDaySavings),
            testId: "recent-wins-30day-savings",
          },
          {
            id: "savings-ytd",
            label: t("enterprise.wins.metricYtdSavings"),
            value: formatCurrency(data.ytdCostSavings),
            testId: "recent-wins-ytd-savings",
          },
          {
            id: "downtime-ytd",
            label: t("enterprise.wins.metricYtdDowntime"),
            value: Math.round(
              Number(data.ytdDowntimeHours) || 0,
            ).toLocaleString(),
            testId: "recent-wins-ytd-downtime",
          },
        ]}
      />

      {data.wins.length === 0 ? (
        <Text tone="subtle" data-testid="recent-wins-empty">
          {t("enterprise.wins.emptyWins")}
        </Text>
      ) : (
        <>
          <Text as="div" size="sm" tone="subtle">
            <strong>{t("enterprise.wins.recentRisksResolvedLabel")}</strong>
          </Text>
          <div className={winsListClassName} data-testid="recent-wins-list">
            {data.wins.map((win) => (
              <WinCard key={win.alertId} win={win} />
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
